import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ProgressBar } from "primereact/progressbar";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";

const ProductionWorkflowMetricsForm = ({
  projectItemId,
  productionItemId,
  productionJobId,
  t,
  onSaved
}) => {
  const [loading, setLoading] = useState(false);
  const [savingWorkflowId, setSavingWorkflowId] = useState(null);
  const [items, setItems] = useState([]);
  const [values, setValues] = useState({});

  const apiCall = (method, url, data) => {
    return axios({
      method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      headers: {
        Authorization: "Bearer " + localStorage.getItem("userToken"),
        "App-Network": localStorage.getItem("networkId")
      }
    });
  };

  useEffect(() => {
    if (!projectItemId) {
      setItems([]);
      setValues({});
      return;
    }

    setLoading(true);

    apiCall("get", `/api/v2/project-item/${projectItemId}/workflow-metrics/anyTime`)
      .then((response) => {
        const payload = response?.data?.data || {};
        setItems(Array.isArray(payload.items) ? payload.items : []);
        setValues({});
      })
      .catch((error) => {
        window.App.toastShow(
          "error",
          t ? t("error") : "Hiba",
          error.toString(),
          "",
          error.response
        );
        setItems([]);
        setValues({});
      })
      .finally(() => {
        setLoading(false);
      });
  }, [projectItemId]);

  const workflowBlocks = useMemo(() => {
    const blocks = [];

    (items || []).forEach((technologyItem) => {
      (technologyItem?.workflows || []).forEach((workflowItem) => {
        const workflow = workflowItem?.workflow;
        const metrics = workflowItem?.metrics || workflow?.metrics;
        const metricsMeasures = workflowItem?.metricsMeasures || [];

        if (!workflow?.id || metricsMeasures.length === 0) {
          return;
        }

        blocks.push({
          technology: technologyItem?.technology || workflowItem?.technology || workflow?.technology,
          workflow,
          metrics,
          metricsMeasures
        });
      });
    });

    return blocks;
  }, [items]);

  const getInputKey = (workflowId, measureId) => {
    return `${workflowId}_${measureId}`;
  };

  const getValue = (workflowId, measureId) => {
    return values[getInputKey(workflowId, measureId)] || "";
  };

  const setValue = (workflowId, measureId, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [getInputKey(workflowId, measureId)]: value
    }));
  };

  const getTargetUrl = (workflowId) => {
    if (productionJobId) {
      return `/api/v2/track-log/workflow/${workflowId}/production-job/${productionJobId}`;
    }

    if (productionItemId) {
      return `/api/v2/track-log/workflow/${workflowId}/production-item/${productionItemId}`;
    }

    return null;
  };

  const saveWorkflow = (block) => {
    const workflowId = block?.workflow?.id;
    const url = getTargetUrl(workflowId);

    if (!workflowId || !url) {
      return;
    }

    const entries = (block.metricsMeasures || [])
      .map((measure) => {
        const rawValue = getValue(workflowId, measure.id);

        return {
          measure: measure.id,
          raw_value: rawValue
        };
      })
      .filter((entry) => {
        return entry.raw_value !== null &&
          entry.raw_value !== undefined &&
          entry.raw_value.toString().trim() !== "";
      });

    if (entries.length === 0) {
      window.App.toastShow("warn", "Nincs rögzíthető adat.");
      return;
    }

    setSavingWorkflowId(workflowId);

    apiCall("post", url, { entries })
      .then(() => {
        window.App.toastShow("success", "Adatok rögzítve.");

        setValues((prevValues) => {
          const nextValues = { ...prevValues };

          (block.metricsMeasures || []).forEach((measure) => {
            delete nextValues[getInputKey(workflowId, measure.id)];
          });

          return nextValues;
        });

        if (typeof onSaved === "function") {
          onSaved();
        }
      })
      .catch((error) => {
        window.App.toastShow(
          "error",
          t ? t("error") : "Hiba",
          error.toString(),
          "",
          error.response
        );
      })
      .finally(() => {
        setSavingWorkflowId(null);
      });
  };

  if (!projectItemId) {
    return null;
  }

  return (
    <div className="p-mt-4">
      <h4 className="p-mb-3">Termelési adatok rögzítése</h4>

      {loading && (
        <ProgressBar mode="indeterminate" style={{ height: "5px" }} />
      )}

      {!loading && workflowBlocks.length === 0 && (
        <Message
          severity="info"
          text="Ehhez jelenleg nincs rögzíthető termelési adat."
        />
      )}

      {!loading && workflowBlocks.map((block) => (
        <div
          key={block.workflow.id}
          className="p-mb-4"
          style={{
            border: "1px solid var(--surface-d)",
            padding: "12px",
            borderRadius: "4px"
          }}
        >
          <div className="p-mb-2" style={{ fontWeight: 700 }}>
            {block.metrics?.name || block.workflow?.name || "Adatrögzítés"}
          </div>

          {block.technology?.name && (
            <div className="p-mb-3" style={{ opacity: 0.7 }}>
              {block.technology.name}
            </div>
          )}

          {(block.metricsMeasures || []).map((measure) => (
            <div
              key={measure.id}
              className="p-field p-grid p-ai-center"
              style={{ marginBottom: "10px" }}
            >
              <label className="p-col-12 p-md-5" style={{ fontWeight: 600 }}>
                {measure.name}
              </label>

              <div className="p-col-12 p-md-5">
                <InputText
                  value={getValue(block.workflow.id, measure.id)}
                  onChange={(e) => setValue(block.workflow.id, measure.id, e.target.value)}
                  keyfilter={
                    measure.type === "int"
                      ? "int"
                      : measure.type === "decimal"
                        ? "num"
                        : undefined
                  }
                  style={{ width: "100%" }}
                />
              </div>

              <div className="p-col-12 p-md-2">
                {measure?.unit?.name || ""}
              </div>
            </div>
          ))}

          <div className="p-text-right">
            <Button
              type="button"
              label="Rögzítés"
              icon="pi pi-save"
              className="p-button-success"
              loading={savingWorkflowId === block.workflow.id}
              onClick={() => saveWorkflow(block)}
              disabled={!productionItemId && !productionJobId}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProductionWorkflowMetricsForm;